import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query'

import { createUser, getCurrentUser, loginUser, updateProfile } from '../auth'
import { queryKeys, queryOptions } from '../react-query-config'
import type {
  CreateUserDTO,
  CreateUserResponse,
  DisableMfaResponseDTO,
  EnableMfaResponseDTO,
  GetMeResponse,
  LoginDTO,
  LoginResponse,
  MfaStatusResponseDTO,
  UpdateProfileDTO,
} from '../types'
import { disableMfa, enableMfa, getMfaStatus } from '../user-management'

// Login hook
export const useLogin = () => {
  const queryClient = useQueryClient()

  return useMutation<LoginResponse, Error, LoginDTO>({
    mutationFn: loginUser,
    onSuccess: async () => {
      // Invalidate and refetch user data after successful login
      await queryClient.invalidateQueries({ queryKey: queryKeys.auth.me() })

      // Refetch user data immediately to ensure fresh data
      await queryClient.refetchQueries({ queryKey: queryKeys.auth.me() })
    },
  })
}

// Check if user has valid access token
const hasValidAccessToken = async (): Promise<boolean> => {
  try {
    const { fetchAuthSession } = await import('aws-amplify/auth')
    const session = await fetchAuthSession()

    return !!session.tokens?.accessToken
  } catch (error) {
    console.error('Error checking access token:', error)

    return false
  }
}

// Get current user hook with conditional fetching based on access token
export const useCurrentUser = (): UseQueryResult<GetMeResponse, Error> => {
  return useQuery<GetMeResponse>({
    queryKey: queryKeys.auth.me(),
    queryFn: getCurrentUser,
    ...queryOptions.auth,
    // Only fetch if we have a valid access token
    enabled: false, // We'll control this manually
    // Disable refetch on window focus to prevent unnecessary quota resets
    refetchOnWindowFocus: false,
    // Refetch on reconnect to handle network issues
    refetchOnReconnect: true,
    // Retry on failure
    retry: (failureCount, error: any) => {
      // Don't retry on 401 (unauthorized) errors
      if (error?.response?.status === 401) {
        return false
      }

      return failureCount < 3
    },
  })
}

// Conditional current user hook that checks for access token before fetching
export const useCurrentUserConditional = (): UseQueryResult<GetMeResponse, Error> => {
  return useQuery<GetMeResponse>({
    queryKey: queryKeys.auth.me(),
    queryFn: getCurrentUser,
    ...queryOptions.auth,
    // Only fetch if we have a valid access token
    enabled: false, // We'll control this manually
    // Disable refetch on window focus to prevent unnecessary quota resets
    refetchOnWindowFocus: false,
    // Refetch on reconnect to handle network issues
    refetchOnReconnect: true,
    // Retry on failure
    retry: (failureCount, error: any) => {
      // Don't retry on 401 (unauthorized) errors
      if (error?.response?.status === 401) {
        return false
      }

      return failureCount < 3
    },
  })
}

// Create user hook
export const useCreateUser = () => {
  const queryClient = useQueryClient()

  return useMutation<CreateUserResponse, Error, CreateUserDTO>({
    mutationFn: createUser,
    onSuccess: () => {
      // Invalidate relevant queries after user creation
      queryClient.invalidateQueries({ queryKey: queryKeys.auth.all })
    },
  })
}

// Update profile hook
export const useUpdateProfile = () => {
  const queryClient = useQueryClient()

  return useMutation<GetMeResponse, Error, UpdateProfileDTO>({
    mutationFn: updateProfile,
    onSuccess: () => {
      // Invalidate and refetch user data after successful profile update
      queryClient.invalidateQueries({ queryKey: queryKeys.auth.me() })
    },
  })
}

// Manual user data refresh hook
export const useRefreshUserData = () => {
  const queryClient = useQueryClient()

  const refreshUserData = async () => {
    try {
      // Check if we have a valid access token before attempting to refresh
      const hasToken = await hasValidAccessToken()

      if (!hasToken) {
        console.log('No valid access token found, skipping user data refresh')

        return false
      }

      // Invalidate and refetch user data
      await queryClient.invalidateQueries({ queryKey: queryKeys.auth.me() })
      await queryClient.refetchQueries({ queryKey: queryKeys.auth.me() })

      return true
    } catch (error) {
      console.error('Error refreshing user data:', error)

      return false
    }
  }

  return { refreshUserData }
}

// Enable MFA hook
export const useEnableMfa = () => {
  const queryClient = useQueryClient()

  return useMutation<EnableMfaResponseDTO, Error>({
    mutationFn: enableMfa,
    onSuccess: () => {
      // Invalidate and refetch user data after successful MFA enable
      queryClient.invalidateQueries({ queryKey: queryKeys.auth.me() })
      queryClient.invalidateQueries({ queryKey: ['mfa', 'status'] })
    },
  })
}

// Disable MFA hook
export const useDisableMfa = () => {
  const queryClient = useQueryClient()

  return useMutation<DisableMfaResponseDTO, Error>({
    mutationFn: disableMfa,
    onSuccess: () => {
      // Invalidate and refetch user data after successful MFA disable
      queryClient.invalidateQueries({ queryKey: queryKeys.auth.me() })
      queryClient.invalidateQueries({ queryKey: ['mfa', 'status'] })
    },
  })
}

// Get MFA status hook
export const useGetMfaStatus = () => {
  return useQuery<MfaStatusResponseDTO>({
    queryKey: queryKeys.auth.mfaStatus(),
    queryFn: getMfaStatus,
    ...queryOptions.auth,
  })
}
